import { watchBusy } from "@frui.ts/screens";
import { validate } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import ChargeManagementService from "services/chargeManagementService";
import EventBus, { putChargeContactFailure, putChargeContactSuccess } from "services/eventBus";
import LocalizationService from "services/localizationService";
import AppRouter from "services/appRouter";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import ChargeManagementEditPartViewModel from "./chargeManagementEditPartViewModel";
import ChargeManagementEditViewModel from "./chargeManagementEditViewModel";
import { attachAutomaticBlurValidator } from "utils/validationPatch";
import ChargeTypesService from "services/chargeTypesService";
import TurnstileViewModel from "../../common/turnstileViewModel";
import React from "react";
import * as Sentry from "@sentry/react";
import { scrollToFirstError } from "../../../utils/scrollTo";
import ChargeOrderService, { validForToNotificationDays } from "services/chargeOrderService";
import NotificationPreferences, { NotificationSalespointKiosValidationRules } from "models/notificationPreferences";
import EnumService from "services/enumService";
import SourceType from "entities/sourceType";
import { NotificationValidationRules } from "entities/notificationPreferencesResponse";
import ChargeValidity from "entities/chargeValidity";
import { bound } from "@frui.ts/helpers";

export default class ChargeManagementContactViewModel extends ChargeManagementEditPartViewModel {
  unsubscriptions: Array<() => void> = [];
  turnstileViewModel: TurnstileViewModel;
  @observable phoneNumberChanged = false;
  @observable initialEmailAddress?: string;
  @observable initialEmailAddressNotification?: string;
  @observable initialPhoneNumberNotification?: string;
  @observable initialNotificationEnabled?: boolean;

  @computed
  get canSubmit() {
    const isAllowed =
      this.charge &&
      this.charge.chargeOrderSourceType &&
      [SourceType.Kiosk, SourceType.Salespoint].includes(this.charge.chargeOrderSourceType);

    const salespointCanSubmit =
      isAllowed &&
      (this.notificationPreferences.emailAddress !== this.initialEmailAddress ||
        this.notificationPreferences._notificationEnabled);

    const eshopCanSubmit =
      this.notificationPreferences.emailAddress !== undefined &&
      this.notificationPreferences.emailAddress.length > 0 &&
      (this.notificationPreferences.emailAddress !== this.initialEmailAddress ||
        this.notificationPreferences._emailAddressNotification !== this.initialEmailAddressNotification ||
        this.notificationPreferences.phoneNumber !== this.initialPhoneNumberNotification ||
        this.notificationEnabled !== this.initialNotificationEnabled);

    return salespointCanSubmit || eshopCanSubmit;
  }

  constructor(
    public localizationService: LocalizationService,
    protected chargeManagementService: ChargeManagementService,
    protected chargeOrderService: ChargeOrderService,
    protected eventBus: EventBus,
    protected enumService: EnumService,
    protected signalRNotificationService: SignalRNotificationService,
    protected chargeTypesService: ChargeTypesService,
    private router: AppRouter,
    turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>
  ) {
    super();
    this.navigationName = "notificationSetup";
    this.turnstileViewModel = turnstileViewModelFactory("chargeManagementContact", true);
    this.unsubscriptions.push(
      this.eventBus.subscribe(putChargeContactFailure, this.putChargeContactFailure),
      this.eventBus.subscribe(putChargeContactSuccess, this.putChargeContactSuccess)
    );
    this.attachValidators();
  }

  protected onInitialize(): Promise<any> | void {
    this.unsubscriptions.push(
      reaction(() => this.notificationPreferences._isNotificationEmailSame, this.onSameNotificationEmailChanged),
      reaction(() => this.notificationPreferences._emailAddressNotification, this.onEmailAddressNotificationChanged),
      reaction(() => this.notificationPreferences.emailAddress, this.onEmailAdressChanged),
      reaction(() => this.notificationPreferences.phoneNumber, this.onPhoneNumberChanged),
      reaction(() => this.notificationPreferences._notificationEnabled, this.onNotificationEnabled)
    );

    return super.onInitialize();
  }

  protected onActivate(): Promise<any> | void {
    this.initialEmailAddress = this.charge?.emailAddress;
    this.initialEmailAddressNotification = this.charge?.notificationPreferences.emailAddress;
    this.initialPhoneNumberNotification = this.charge?.notificationPreferences.phoneNumber;
    this.initialNotificationEnabled =
      this.charge?.notificationPreferences.emailNotificationEnabled ||
      this.charge?.notificationPreferences.smsNotificationEnabled;

    if (this.charge?.notificationPreferences && this.notificationPreferences) {
      this.notificationPreferences._emailAddressNotification = this.charge?.notificationPreferences.emailAddress;
      this.notificationPreferences._notificationEnabled =
        !!this.charge?.notificationPreferences.emailAddress && !!this.charge?.notificationPreferences.emailNotificationEnabled;
      this.notificationPreferences._isNotificationEmailSame = this.charge?.emailAddress
        ? this.charge?.emailAddress === this.charge?.notificationPreferences.emailAddress
        : false;
      this.notificationPreferences.phoneNumber = this.charge?.notificationPreferences.phoneNumber;
      this.notificationPreferences.emailAddress = this.charge?.emailAddress;
      this.notificationPreferences._emailConfirmation = this.charge?.emailAddress;
    }
    if (this.charge?.emailAddress && this.notificationPreferences) {
      this.notificationPreferences.emailAddress = this.notificationPreferences._emailConfirmation = this.charge.emailAddress;
    }
    if (this.notificationEnabledInfo !== undefined && this.notificationPreferences) {
      this.notificationPreferences.emailNotificationEnabled = this.notificationPreferences._notificationEnabled = true;
    }
    if (
      this.charge?.notificationPreferences.emailNotificationEnabled ||
      this.charge?.notificationPreferences.smsNotificationEnabled
    ) {
      this.notificationEnabled = true;
    }
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  @computed
  get defaultPrefixNumber() {
    if (!this.phoneNumberChanged) {
      return this.localizationService.defaultPrefixNumber;
    }
    return undefined;
  }
  @computed
  get allowedRemoveEmail() {
    return (
      this.charge &&
      this.charge.chargeOrderSourceType &&
      [SourceType.Kiosk, SourceType.Salespoint].includes(this.charge.chargeOrderSourceType)
    );
  }

  @computed
  get canEdit() {
    if (this.success) {
      return false;
    }

    const permittedActions = this.charge?.permittedActions;
    return (
      (permittedActions?.emailAddressChange.isAllowed ||
        permittedActions?.emailNotificationPreferencesChange.isAllowed ||
        permittedActions?.smsNotificationPreferencesChange.isAllowed) === true
    );
  }

  @computed
  get canCheckNotif() {
    return this.canEdit && (this.canEditEmailNotif || this.canEditSmsNotif);
  }

  @computed
  get canEditEmailNotif() {
    return this.permittedActions && this.permittedActions.emailNotificationPreferencesChange.isAllowed;
  }

  @computed
  get canEditSmsNotif() {
    return this.permittedActions && this.permittedActions.smsNotificationPreferencesChange.isAllowed;
  }

  @computed
  get charge() {
    return this.chargeManagementService.charge;
  }

  @computed
  get hasSecondStep() {
    return false;
  }

  @computed
  get notificationPreferences() {
    return (this.charge?.notificationPreferences as NotificationPreferences) ?? new NotificationPreferences();
  }

  @computed
  get permittedActions() {
    return this.charge?.permittedActions;
  }

  @computed
  get notificationEnabled() {
    return this.notificationPreferences._notificationEnabled;
  }

  set notificationEnabled(notificationEnabled) {
    if (notificationEnabled && this.notificationPreferences) {
      this.notificationPreferences._notificationEnabled = notificationEnabled;
    }
    if (!notificationEnabled && this.notificationPreferences) {
      this.notificationPreferences._notificationEnabled = false;
    }
  }

  @computed
  get emailNotificationEnabled() {
    return this.notificationPreferences.emailNotificationEnabled;
  }

  @computed
  get showNotificationPreferences() {
    return this.canCheckNotif;
  }

  @computed
  get isNotificationEmailSame() {
    return this.notificationPreferences._isNotificationEmailSame;
  }

  @computed
  get smsNotificationEnabled() {
    return this.notificationPreferences.smsNotificationEnabled;
  }

  @computed
  get customInfoMessage() {
    let message;
    if (!this.canEdit && !this.canEditSmsNotif && !this.canEditEmailNotif) {
      message = this.permittedActions?.emailAddressChange.rejectionReason;
    }
    if (this.canEdit && this.canEditSmsNotif && !this.canEditEmailNotif) {
      message = this.permittedActions?.smsNotificationPreferencesChange.rejectionReason;
    }
    if (this.canEdit && !this.canEditSmsNotif && this.canEditEmailNotif) {
      message = this.permittedActions?.emailNotificationPreferencesChange.rejectionReason;
    }
    if (this.charge) {
      const chargeType = this.chargeTypesService.getPriceListItemChargeType(this.charge.priceListItemId, true);
      if (chargeType?.validFor === ChargeValidity.SingleDay) {
        return this.translate("notificationSetup.DENIED_FOR_CHARGE_TYPE").replace("%CHARGE_TYPE%", chargeType.description);
      }
    }
    if (message && this.charge) {
      const chargeType = this.chargeTypesService.getPriceListItemChargeType(this.charge.priceListItemId, true);
      return chargeType?.description
        ? this.translate(`notificationSetup.${message}`).replace("%CHARGE_TYPE%", chargeType.description)
        : this.translate(`notificationSetup.${message}`);
    }
  }

  @computed
  get notificationEnabledInfo() {
    if (!this.charge || !this.notificationPreferences?._notificationEnabled) {
      return undefined;
    }

    const tg = (code: string) => this.localizationService.translateGeneral(`charge.management.edit.notificationSetup.${code}`);
    const chargeType = this.chargeTypesService.getPriceListItemChargeType(this.charge.priceListItemId, true);
    if (!chargeType?.notifyDaysBefore || this.chargeOrderService.chargeOrder.charges.length > 1) {
      return tg("notificationEnabledInfoGeneric");
    }

    return validForToNotificationDays(tg, chargeType.notifyDaysBefore);
  }

  @action.bound
  attachValidators() {
    if (this.notificationPreferences && this.allowedRemoveEmail === false) {
      attachAutomaticBlurValidator(this.notificationPreferences, NotificationValidationRules);
    }
    if (this.allowedRemoveEmail) {
      attachAutomaticBlurValidator(this.notificationPreferences, NotificationSalespointKiosValidationRules);
    }
  }

  @action.bound
  onEmailAddressNotificationChanged() {
    const isEmailNotificationSet =
      this.notificationPreferences._emailAddressNotification !== undefined &&
      this.notificationPreferences._emailAddressNotification.length > 0;
    this.notificationPreferences.emailNotificationEnabled = isEmailNotificationSet;
  }

  @action.bound
  onPhoneNumberChanged() {
    this.phoneNumberChanged = true;
    const isPhoneNumberSet =
      this.notificationPreferences.phoneNumber !== undefined && this.notificationPreferences.phoneNumber.length > 0;
    this.notificationPreferences.smsNotificationEnabled = isPhoneNumberSet;
  }

  @action.bound
  onSameNotificationEmailChanged() {
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
      return;
    }
    this.notificationPreferences._emailAddressNotification = undefined;
  }

  @action.bound
  onEmailAdressChanged() {
    if (this.notificationPreferences._isNotificationEmailSame) {
      this.notificationPreferences._emailAddressNotification = this.notificationPreferences.emailAddress;
    }
    if (!this.notificationPreferences.emailAddress) {
      this.notificationPreferences._isNotificationEmailSame = false;
    }
  }

  @action.bound
  onNotificationEnabled() {
    if (!this.notificationPreferences._notificationEnabled && !this.allowedRemoveEmail) {
      this.notificationPreferences.phoneNumber = undefined;
      this.notificationPreferences._emailAddressNotification = undefined;
      this.notificationPreferences._isNotificationEmailSame = false;
      this.notificationPreferences.emailNotificationEnabled = false;
      this.notificationPreferences.smsNotificationEnabled = false;
    }
  }

  @action.bound
  protected putChargeContactSuccess(data: ReturnType<typeof putChargeContactSuccess>) {
    if (data.payload.triggerer !== "contactChange") return;
    this.clearAfterAsyncAction();

    if (!this.charge) return;
    this.success = true;

    this.charge._canBeChanged = false;
    this.charge.notificationPreferences.smsNotificationEnabled = data.payload.notificationPreferences?.smsNotificationEnabled;
    this.charge.notificationPreferences.emailNotificationEnabled = data.payload.notificationPreferences?.emailNotificationEnabled;
    this.charge.notificationPreferences._emailAddressNotification =
      data.payload.notificationPreferences?._emailAddressNotification;
    this.charge.notificationPreferences._emailConfirmation = data.payload.emailAddress;
    this.charge.emailAddress = data.payload.emailAddress;
    this.charge.notificationPreferences.phoneNumber = data.payload.notificationPreferences?.phoneNumber;
    if (data.payload.authCode) this.charge.authCode = data.payload.authCode;
    Object.assign(this.charge.notificationPreferences, data.payload.notificationPreferences);

    this.router.navigate(ChargeManagementEditViewModel, undefined, {
      countryId: this.charge.vehicle.countryId,
      licensePlate: this.charge.vehicle.licensePlate,
      authCode: this.charge.authCode,
    });
  }

  @action.bound
  protected putChargeContactFailure(data: ReturnType<typeof putChargeContactFailure>) {
    if (data.payload.triggerer !== "contactChange") {
      return;
    }

    this.clearAfterAsyncAction();
    this.setErrorMessageBasedOnType(data.payload.type);
  }

  @action.bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`charge.management.edit.${code}`);
  }

  @action.bound
  ta(code: string) {
    return this.localizationService.translateAttribute("contact", code);
  }

  /*  onTurnstileVerify = async (turnstileToken: string) => {
    if (turnstileToken) {
      this.turnstileToken = turnstileToken;
    }
  }; */

  @action.bound
  @watchBusy
  async submit() {
    this.success = false;
    this.errorMessage = undefined;
    const notificationPreferences = this.notificationPreferences;

    if (!notificationPreferences) return;
    if (!this.validate()) {
      scrollToFirstError(this.formElement);
      return;
    }

    if (!this.turnstileViewModel.turnstileToken) return;

    this.isLoading = true;

    try {
      await this.chargeManagementService.editContact(
        this.notificationPreferences,
        "contactChange",
        this.turnstileViewModel.turnstileToken
      );
    } finally {
      this.isLoading = false;
    }
  }

  @bound
  public validate() {
    return validate(this.notificationPreferences);
  }
}
